import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { useCreateRequirementMapping } from "@src/appV2/RequirementMapping/api/useCreateRequirementMapping";
import {
  type CreateRequirementMappingDto,
  Domain,
  MappingLevel,
  REQUIREMENT_MAPPING_RESOURCE,
  REQUIREMENT_RESOURCE,
} from "@src/appV2/RequirementMapping/types";
import { type UseModalState } from "@src/lib/ui-react";
import { isAxiosError } from "axios";
import { useState } from "react";

import { useCreateTen99PolicyEntity } from "../api/useCreateTen99PolicyEntity";

interface UseEnableTen99PolicyCoverageParams {
  workplaceId: string;
  suggestedCategoryCode: string;
  documentRequirementId: string;
  qualifications: string[];
  workplaceTypes: string[];
  modalState: UseModalState;
  onSuccess: () => void;
}

interface UseEnableTen99PolicyCoverageResult {
  isSubmitting: boolean;
  mappingFailed: boolean;
  mappingErrorMessage: string;
  handleSubmit: () => Promise<void>;
  handleRetryMapping: () => Promise<void>;
}

export function useEnableTen99PolicyCoverage(
  params: UseEnableTen99PolicyCoverageParams
): UseEnableTen99PolicyCoverageResult {
  const {
    workplaceId,
    suggestedCategoryCode,
    documentRequirementId,
    qualifications,
    workplaceTypes,
    modalState,
    onSuccess,
  } = params;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mappingFailed, setMappingFailed] = useState(false);
  const [mappingErrorMessage, setMappingErrorMessage] = useState("");

  const createEntityMutation = useCreateTen99PolicyEntity();
  const createMappingMutation = useCreateRequirementMapping();

  const buildMappingPayload = (): CreateRequirementMappingDto => ({
    data: {
      type: REQUIREMENT_MAPPING_RESOURCE,
      attributes: {
        level: MappingLevel.HCF,
        qualifications,
        workplaceTypes,
        requiredBy: [workplaceId],
        requiredForBooking: true,
        domain: Domain.FLEX,
        isOrientationRequirement: false,
      },
      relationships: {
        requirement: {
          data: {
            id: documentRequirementId,
            type: REQUIREMENT_RESOURCE,
          },
        },
      },
    },
  });

  const handleMappingError = (error: unknown) => {
    logError(APP_V2_APP_EVENTS.CREATE_TEN99_POLICY_REQUIREMENT_MAPPING_FAILURE, {
      error,
      metadata: { workplaceId, documentRequirementId },
    });

    const defaultMessage =
      "Entity was created but requirement mapping failed. You can retry or create the mapping manually.";
    const errorMessage = isAxiosError(error)
      ? ((error.response?.data as { message?: string })?.message ?? defaultMessage)
      : defaultMessage;

    setMappingFailed(true);
    setMappingErrorMessage(errorMessage);
  };

  const handleSubmit = async () => {
    if (qualifications.length === 0) {
      showErrorToast("Please select at least one qualification");
      return;
    }

    setIsSubmitting(true);

    try {
      await createEntityMutation.mutateAsync({
        workplaceId,
        categoryCode: suggestedCategoryCode,
      });

      try {
        await createMappingMutation.mutateAsync(buildMappingPayload());
      } catch (mappingError: unknown) {
        handleMappingError(mappingError);
        return;
      }

      showSuccessToast("1099 Policy coverage enabled successfully");
      onSuccess();
      modalState.closeModal();
    } catch (error: unknown) {
      let errorMessage = "Failed to enable 1099 Policy coverage";
      if (isAxiosError(error)) {
        errorMessage = (error.response?.data as { message?: string })?.message ?? errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      showErrorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetryMapping = async () => {
    setIsSubmitting(true);

    try {
      await createMappingMutation.mutateAsync(buildMappingPayload());
      showSuccessToast("Requirement mapping created successfully");
      setMappingFailed(false);
      onSuccess();
      modalState.closeModal();
    } catch (error: unknown) {
      handleMappingError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    mappingFailed,
    mappingErrorMessage,
    handleSubmit,
    handleRetryMapping,
  };
}
